import { Component, OnInit, inject, effect, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { Entity } from '../../objects/entity';
import { CoiService } from '../../service/entity-services/coi.service';
import { Coi } from '../../objects/coi';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';

import { FormsModule } from '@angular/forms';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-coi-list',
    templateUrl: './coi-list.component.html',
    styleUrls: ['./coi-list.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      MatProgressBar,
      MatButtonToggleModule,
      MatTableModule,
      MatSortModule,
      MatIconModule,
      MatChipsModule,
      MatTooltipModule
    ]
  })
export class CoiListComponent extends Entity implements OnInit {

  public filterService = inject(MyWorkFilterService);
  private cdr = inject(ChangeDetectorRef);

  public allCois: Coi[] = [];
  public filteredCois: Coi[] = [];

  waitingForFilterResults = false;
  isLoading = true;

  // Expose MyWorkFilter enum to template
  MyWorkFilter = MyWorkFilter;

  // Entity type display name
  entityTypeName = 'COIs';

  displayedColumns: string[] = [
    'status',
    'insuredPartyFriendlyName',
    'coverageTypes',
    'primaryOwner',
    'expirationDate',
    'insuranceProviderNameDisplay'
  ];

  constructor(public router: Router, public coiService: CoiService) {
    super();

    // Set up effect to watch for filter changes and user email resolution
    effect(() => {
      // Read signals to establish dependencies
      const activeFilters = this.filterService.activeFilters();
      const userEmail = this.filterService.currentUserEmail();

      // Apply filtering whenever filters, user email, or COIs change
      if (this.allCois.length > 0 && userEmail) {
        this.isLoading = false;
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  async ngOnInit() {
    // Subscribe to visible (non-archived, permission-filtered) COIs
    this.coiService.visibleCois$.subscribe(cois => {
      this.allCois = cois;
      console.log(`Successfully retrieved ${this.allCois.length} COIs.`);

      // Only apply filters if we have COIs and user email is resolved
      if (this.allCois.length > 0 && this.filterService.currentUserEmail()) {
        this.isLoading = false;
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Applies current filter service filters to the COIs list
   */
  private applyFilters(): void {
    this.filteredCois = this.filterService.filterCois(this.allCois);
    console.log(`Filtered to ${this.filteredCois.length} COIs`);
  }

  /**
   * Handler for filter button toggle changes
   */
  userChangedFilter(event: any): void {
    const selectedValues: string[] = event.value || [];

    // Clear all filters first
    this.filterService.clearFilters();

    // Add each selected filter
    selectedValues.forEach(value => {
      switch (value) {
        case 'Owner':
          this.filterService.addFilter(MyWorkFilter.Owner);
          break;
        case 'ExpiringSoon':
          this.filterService.addFilter(MyWorkFilter.ExpiringSoon);
          break;
        case 'Expired':
          this.filterService.addFilter(MyWorkFilter.Expired);
          break;
        case 'NextReviewDateSoon':
          this.filterService.addFilter(MyWorkFilter.NextReviewDateSoon);
          break;
      }
    });
  }

  /**
   * Checks if a filter is currently active
   */
  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.isFilterActive(filter);
  }

  onRowClicked(coi: Coi): void {
    this.router.navigate(['/securehome/coi', coi.coiNum]);
  }

  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.applyFilters();
      return;
    }

    this.filteredCois = [...this.filteredCois].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'insuredPartyFriendlyName': return this.compare(this.getInsuredPartyName(a), this.getInsuredPartyName(b), isAsc);
        case 'coverageTypes': return this.compare(this.formatCoverageTypes(a.coverageTypes), this.formatCoverageTypes(b.coverageTypes), isAsc);
        case 'primaryOwner': return this.compare(this.getPrimaryOwnerName(a), this.getPrimaryOwnerName(b), isAsc);
        case 'expirationDate': return this.compare(a.expirationDate, b.expirationDate, isAsc);
        case 'insuranceProviderNameDisplay': return this.compare(this.getInsuranceProviderName(a), this.getInsuranceProviderName(b), isAsc);
        default: return 0;
      }
    });
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    } catch {
      return dateString;
    }
  }

  formatCoverageTypes(types: string[] | undefined): string {
    if (!types || !Array.isArray(types) || types.length === 0) return '';
    return types.join(', ');
  }

  /**
   * Extract a display name from a field that may be a string, object, or array.
   * Handles hydrated user/party objects from the backend.
   */
  extractDisplayName(value: any, primaryKey: string = 'name', fallbackKey?: string): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => this.extractDisplayName(v, primaryKey, fallbackKey)).filter(n => n).join(', ');
    }
    if (typeof value === 'object') {
      if (value[primaryKey]) return value[primaryKey];
      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
      // Try common fallback fields
      if (value.friendlyName) return value.friendlyName;
      if (value.name) return value.name;
      if (value.email) return value.email;
      // Handle vendorData wrapper
      if (value.vendorData?.name) return value.vendorData.name;
      // Handle legalEntityData wrapper
      if (value.legalEntityData?.legalEntityName) return value.legalEntityData.legalEntityName;
    }
    return '';
  }

  getInsuredPartyName(coi: Coi): string {
    return coi.insuredPartyFriendlyName || this.extractDisplayName(coi.insuredParty) || '';
  }

  getInsuranceProviderName(coi: Coi): string {
    return coi.insuranceProviderNameDisplay || this.extractDisplayName(coi.insuranceProvider) || '';
  }

  getPrimaryOwnerName(coi: Coi): string {
    return coi.primaryOwnerFriendlyName || this.extractDisplayName(coi.primaryOwner, 'friendlyName', 'email') || '';
  }

  isExpired(coi: Coi): boolean {
    if (!coi.expirationDate) return false;
    return new Date(coi.expirationDate) < new Date();
  }

  isExpiringSoon(coi: Coi): boolean {
    if (!coi.expirationDate) return false;
    const expDate = new Date(coi.expirationDate);
    const now = new Date();
    const daysUntil = (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntil >= 0 && daysUntil <= 180;
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    if (!a && !b) return 0;
    if (!a) return isAsc ? -1 : 1;
    if (!b) return isAsc ? 1 : -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
